import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../../apiHelper';

import legacyTheme from '../../theme';
import { NextThemeProvider, tokens } from '../../theme-next';
import { DOCUMENT_LABELS } from './config/documentLabels';
import { CgAuditBatchApiResponse, logGlobalIaReport, traceIa } from './iaTrace';
import { CgDocType } from './ocr/ocrResponseTypes';
import { IngestionItem, useFormCGIAStore } from './store/useFormCGIAStore';
import type { UploadedDocResult } from './store/useFormCGIAStore';
import { AlertsColumn } from './v2/AlertsColumn';
import { type AuditBatchResult, type AuditVerdict, auditResultToFindings } from './v2/auditVerdictAdapter';
import { DocRow, DocRowStatus, DocumentChecklistColumn } from './v2/DocumentChecklistColumn';
import { HeroCard } from './v2/HeroCard';
import { MultiDropzone } from './v2/MultiDropzone';
import { deriveCotitulaireFlags, resolveAcquereurCni, resolveCotitulairePiFromBatch } from './v2/acquereurReconcile';
import { selectReceivedUploads } from './v2/failedUploads';
import { ParcoursBanner } from './v2/ParcoursBanner';
import {
  PARCOURS_SOURCE_DETAILS,
  buildSuccessionDeclarationMismatchFinding,
  detectParcours,
} from './v2/parcoursDetection';
import { buildEffectiveDocQueue, shouldListVersoRow } from './v2/buildEffectiveDocQueue';
import { pickSlotRepresentatives } from './v2/docRowSelection';
import { CertifySubmissionModal } from './v2/CertifySubmissionModal';
import { PlateAndDepartmentScreen } from './v2/PlateAndDepartmentScreen';
import { RecapAgencyScreen } from './v2/RecapAgencyScreen';
import { TaxConfirmationScreen } from './v2/TaxConfirmationScreen';
import { VerifyInfoScreen } from './v2/VerifyInfoScreen';
import { ctvoV1ParcoursToBannerLabel, ctvoV1ResultToFindings } from './v2/ctvoAnalyzeV1Adapter';
import { useAutoExtractQueue } from './v2/hooks/useAutoExtractQueue';
import { useAgencyFeatureFlag } from '../../hooks/useAgencyFeatureFlag';
import { useAgencyFees } from '../../hooks/useApiQueries';
import { useCtvoAnalyzeV1 } from '../../hooks/useCtvoAnalyzeV1';
import { useAuth } from '../AuthContext';
import { useBackgroundUploads } from '../BackgroundUploads/BackgroundUploadsProvider';
import { CrossCheckFinding, runCrossChecks } from './validation/crossCheckEngine';
import { ALL_CROSS_CHECK_RULES } from './validation/crossCheckRules';
import { isCtRequiredFromSession } from './validation/ctRequirement';
import {
  PM_NOT_REQUIRED_DOC_TYPES,
  isAcheteurPersonneMorale,
  isVendeurPersonneMorale,
} from './validation/personneMorale';
import { permisDeposeVautPieceIdentite } from './validation/permisPieceIdentite';
import { filterFindingsForDisplay } from './v2/findingsDisplay';

const makeIngestionItemId = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`;

// Messages verbeux façon "jauge de l'huile" qui rappellent que l'analyse peut
// prendre plusieurs secondes. Rotation toutes les 20 s tant qu'il y a des
// analyses en cours. Ton voulu : rassurant, automobile, léger humour.
const LOADING_MESSAGES = [
  'Chauffage des cylindres IA…',
  'On essuie le pare-brise, on regarde la carte grise de près…',
  "Vérification des pneus et des dates de CT pendant qu'on y est…",
  'Petit coup de polish sur les VIN avant comparaison…',
  'Les prompts sont sous le capot, ça peut prendre quelques secondes.',
  'Inspection minutieuse des signatures — pas de tour de piste sans ça.',
  "Le carburant Haiku est fluide, mais l'IA relit deux fois par sécurité.",
  "Analyse en cours, n'hésitez pas à siroter un café ☕️",
];

const useRotatingLoadingMessage = (active: boolean) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 20_000);
    return () => clearInterval(id);
  }, [active]);
  return LOADING_MESSAGES[index];
};

/**
 * Construit les lignes affichées dans la DocumentChecklistColumn à partir
 * des docs attendus (queue) et des ingestionItems en cours.
 * La ligne reflète le dernier ingestionItem qui a ciblé ce docType
 * (priorité au plus récent `createdAt`).
 *
 * En mode édition (chip "Pièces à déposer"), on injecte 2 statuts spéciaux :
 *  - `requested` (orange "À redéposer") pour les docTypes qui ont une openRequest
 *  - `validated_locked` (vert cadenas) pour les docTypes déjà validés par GOD
 *  Une fois l'agence redepose une pièce demandée, l'ingestionItem prend le
 *  dessus (uploading/analyzing/ok) — ce qui fait basculer la ligne en `ok`.
 */
interface EditModeRowContext {
  isEditMode: boolean;
  requestedDocTypes: Set<CgDocType>;
  requestReasonByDocType: Record<string, string | null>;
  validatedLockedDocTypes: Set<CgDocType>;
}

/**
 * Une pièce d'identité (permis / CNI) a-t-elle été déposée SANS sa face
 * d'identité (recto carte / couverture rose ; côté photo de la CNI) ? Dans ce
 * cas elle ne doit PAS apparaître auto-validée : l'identité n'est pas
 * vérifiable. Reflète côté checklist ce que bloquent les règles cross-check
 * `permis_recto_manquant` / `piece_identite_recto_manquant`.
 *  - permis : verso (catégories/MRZ) ou pages intérieures rose → aucune identité.
 *  - CNI : le verso porte l'identité via le MRZ ; on s'appuie donc sur le flag
 *    explicite `face_identite_presente` (présence de la photo) du modèle.
 */
const isIdentityFaceMissing = (doc?: UploadedDocResult): boolean => {
  const fields = doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined;
  // UNIQUEMENT le flag explicite face_identite_presente=false. On NE déduit PAS
  // l'absence de recto d'un « tous les champs identité vides » : un permis reçu
  // via un fichier MULTI-DOCUMENTS (CNI + permis, warning
  // contient_aussi_permis_conduire) est enregistré champs vides alors qu'il est
  // présent. Iso règle cross-check permis_recto_manquant (cas DC-39460).
  return fields?.['face_identite_presente']?.value === false;
};

const buildDocRows = (
  queue: readonly CgDocType[],
  ingestionItems: Record<string, IngestionItem>,
  editCtx?: EditModeRowContext,
  notRequiredDocTypes?: ReadonlySet<CgDocType>,
  uploadedDocs?: Partial<Record<CgDocType, UploadedDocResult>>,
  titulaireCniUrl?: string,
  /** Pièces dont la personne a été choisie par défaut (cf. identityAmbiguousUrls). */
  ambiguousUrls?: readonly string[],
): DocRow[] => {
  // Quand plusieurs fichiers du même type sont déposés (recto + verso, doublons,
  // ou scans multi-documents contenant aussi une autre pièce), on ne garde que
  // le MEILLEUR représentant pour le statut de la checklist : une pièce
  // analysée avec succès (statut 'ok', confiance la plus haute) prime sur un
  // scan partiel (verso seul, faible confiance) ou en erreur. Sinon la pièce
  // s'affichait « à vérifier » alors qu'un scan complet OK existait — cas
  // DC-39155 : permis recto/verso + CNI répartis sur plusieurs scans, le plus
  // récent (verso seul, 82 %) gagnait à tort sur un scan complet à 97 %.
  // Les items NON retenus ici (verso, doublon) ne disparaissent pas : ils sont
  // remontés par `selectReceivedUploads` dans le sous-bloc « Autres pièces
  // reçues » de la checklist.
  const byDocType = pickSlotRepresentatives(ingestionItems, titulaireCniUrl);

  return queue.map((docType) => {
    const label = DOCUMENT_LABELS[docType];
    const match = byDocType.get(docType);

    let status: DocRowStatus = 'pending';
    let subtitle = label.subtitle;
    let requestReason: string | null | undefined;

    if (match) {
      if (match.status === 'uploading') {
        status = 'analyzing';
        subtitle = 'Téléversement en cours…';
      } else if (match.status === 'analyzing') {
        status = 'analyzing';
        subtitle = 'Analyse IA en cours…';
      } else if (match.status === 'ok') {
        // Auto-validation : on privilégie le booléen STABLE `docTypeMatch` (le
        // modèle d'extraction a explicitement affirmé que le document correspond
        // au type attendu) plutôt que la seule confiance numérique. Sinon une
        // pièce légitime dont la confiance oscille autour de 85 % d'un run à
        // l'autre (ex : lettre d'assurance AGPM lue à 82 %, cas DC-39153)
        // bascule entre « AUTO-VALIDÉ » et « À VÉRIFIER » — d'où le « manque de
        // cohérence » perçu. Fallback sur le seuil de confiance quand le booléen
        // est absent (pièce détectée dans un fichier multi-docs, item ancien).
        const typeConfirmed = match.docTypeMatch === true || (match.confidence ?? 0) >= 85;
        status = typeConfirmed ? 'ok' : 'verifier';
        subtitle = match.fileName;
        // Garde face d'identité : un permis / une CNI dont la face identité
        // (recto/couverture) manque ne doit PAS rester auto-validé — l'identité
        // n'est pas vérifiable. On reflète ici les règles cross-check dédiées.
        if (
          (docType === 'permis_conduire' || docType === 'piece_identite') &&
          isIdentityFaceMissing(uploadedDocs?.[docType])
        ) {
          status = 'reupload';
          subtitle =
            docType === 'permis_conduire'
              ? 'Face identité manquante — redéposez le recto / la couverture du permis (photo + nom/prénom).'
              : "Face identité manquante — redéposez le recto de la pièce d'identité (côté photo).";
        }
      } else if (match.status === 'unreadable') {
        status = 'reupload';
        subtitle = 'Document illisible, merci de le redéposer';
      } else if (match.status === 'mismatch') {
        // Mode édition : le type ne matche pas une openRequest (l'agence a uploadé
        // le mauvais type pour la pièce demandée) — message contextualisé.
        if (editCtx?.isEditMode && editCtx.requestedDocTypes.has(docType)) {
          status = 'reupload';
          subtitle = `Cette pièce semble être un ${match.docTypeDetected ?? 'autre document'} — nous attendions ${label.title}.`;
        } else {
          status = 'reupload';
          subtitle = 'Type de document non reconnu';
        }
      } else if (match.status === 'error') {
        status = 'reupload';
        subtitle = match.errorMessage ?? 'Erreur pendant le traitement';
      }
    } else if (
      notRequiredDocTypes?.has(docType) &&
      !editCtx?.requestedDocTypes.has(docType) &&
      !editCtx?.validatedLockedDocTypes.has(docType)
    ) {
      // Pièce non exigible (ex: CT sur véhicule ≤ 4 ans) et pas de dépôt —
      // affichée grisée pour expliquer pourquoi on ne l'attend pas. Si
      // l'agence dépose quand même la pièce, le match ci-dessus reprend la
      // main ; si GOD la demande ou l'a déjà validée, ces statuts priment.
      status = 'not_required';
      // Pièce non exigible : le badge « NON REQUIS » suffit. Pas de sous-titre
      // explicatif — info non nécessaire (retour Luc 2026-07). « Si ya pas
      // besoin, ya pas besoin et c'est tout. »
      subtitle = '';
    } else if (editCtx?.isEditMode) {
      // Pas de nouveau dépôt — branchement spécial mode édition
      if (editCtx.requestedDocTypes.has(docType)) {
        status = 'requested';
        requestReason = editCtx.requestReasonByDocType[docType] ?? null;
        subtitle = requestReason
          ? `Skipcar attend cette pièce. ${requestReason}`
          : 'Skipcar attend cette pièce — déposez-la pour finaliser le dossier.';
      } else if (editCtx.validatedLockedDocTypes.has(docType)) {
        status = 'validated_locked';
        subtitle = 'Pièce déjà acceptée par Skipcar';
      }
    } else if (uploadedDocs?.[docType]) {
      // Pièce reçue sans ingestion item dédié : détectée dans un fichier
      // multi-documents (ex : PDF CNI + permis → warning contient_aussi_*).
      status = 'ok';
      subtitle = 'Détectée dans un fichier multi-documents';
    }

    // Réassignation manuelle : proposée uniquement là où le routage a dû
    // choisir la personne par défaut (verso sans identité lisible).
    // Lien de consultation : le slot routé d'abord (source de vérité après
    // routage identité), sinon le fichier de l'item d'ingestion — une pièce en
    // cours d'analyse ou rejetée doit rester consultable.
    const imageUrl = uploadedDocs?.[docType]?.firebaseUrls[0] ?? match?.firebaseUrl;
    const personUncertain = Boolean(imageUrl && ambiguousUrls?.includes(imageUrl));
    // Une pièce dont on demande à qui elle appartient ne peut pas s'afficher
    // « AUTO-VALIDÉ » : le badge affirmerait une certitude qu'on vient
    // justement de dire ne pas avoir (retour Luc 2026-07-30).
    const effectiveStatus: DocRowStatus = personUncertain && status === 'ok' ? 'verifier' : status;

    return { docType, title: label.title, subtitle, status: effectiveStatus, requestReason, imageUrl, personUncertain };
  });
};

const FormCGIAv2Inner = () => {
  const v2Step = useFormCGIAStore((s) => s.v2Step);
  const startV2 = useFormCGIAStore((s) => s.startV2);
  const setV2Step = useFormCGIAStore((s) => s.setV2Step);
  const setTaxConfirmReturn = useFormCGIAStore((s) => s.setTaxConfirmReturn);
  const ingestionItems = useFormCGIAStore((s) => s.ingestionItems);
  const enqueueIngestionItems = useFormCGIAStore((s) => s.enqueueIngestionItems);
  const uploadedDocs = useFormCGIAStore((s) => s.uploadedDocs);
  const parcours = useFormCGIAStore((s) => s.parcours);
  const extractedFields = useFormCGIAStore((s) => s.extractedFields);
  const updateExtractedFieldValue = useFormCGIAStore((s) => s.updateExtractedFieldValue);
  const coBuyerFiles = useFormCGIAStore((s) => s.coBuyerFiles);
  const setCoBuyerFiles = useFormCGIAStore((s) => s.setCoBuyerFiles);
  const identityAmbiguousUrls = useFormCGIAStore((s) => s.identityAmbiguousUrls);
  const reassignIdentityPerson = useFormCGIAStore((s) => s.reassignIdentityPerson);
  const storedRegPlate = useFormCGIAStore((s) => s.regPlate);
  const storedDepartment = useFormCGIAStore((s) => s.department);
  const isSuccession = useFormCGIAStore((s) => s.isSuccession);
  const isProfessionalResale = useFormCGIAStore((s) => s.isProfessionalResale);
  const storedTaxData = useFormCGIAStore((s) => s.taxData);
  const resetV2 = useFormCGIAStore((s) => s.resetV2);
  const serializeV2Snapshot = useFormCGIAStore((s) => s.serializeV2Snapshot);
  // ── Mode édition (chip "Pièces à déposer" → /form-cgia?registrationId=X) ──
  const editingRegistrationId = useFormCGIAStore((s) => s.editingRegistrationId);
  const openDocumentRequests = useFormCGIAStore((s) => s.openDocumentRequests);
  const validatedLockedDocTypes = useFormCGIAStore((s) => s.validatedLockedDocTypes);
  const nextStatusOnFulfill = useFormCGIAStore((s) => s.nextStatusOnFulfill);
  const hydrateV2FromAgencyEditContext = useFormCGIAStore((s) => s.hydrateV2FromAgencyEditContext);

  const { currentUser } = useAuth();
  // Seul un utilisateur "god" (interne Skipcar) voit les findings INFO : ce sont
  // des notes de diagnostic (stub MiSIV en dev, plaque à
  // confirmer…) utiles à l'audit interne mais anxiogènes/parasites pour l'agence.
  const isGod = (currentUser as any)?.data?.data?.role === 'god';
  const agencyId =
    (currentUser as any)?.data?.organization?.value ?? (currentUser as any)?.data?.data?.idPipedrive ?? null;
  const { data: agencyFeesData } = useAgencyFees(agencyId);
  const prestationHT: number = agencyFeesData?.data?.cgFee ?? 10;

  const { startIngestion, ingestUploadedUrls, abortIngestionSession } = useAutoExtractQueue();

  /**
   * Dépôt des pièces d'identité du CO-TITULAIRE acquéreur.
   *
   * On garde la liste d'URLs telle quelle (elle continue d'alimenter
   * `coBuyerFilesUrls`, donc `CG_Documents.Type = 'coBuyerFiles'` et list-cg :
   * rien ne change de ce côté) et on AJOUTE l'analyse OCR des fichiers
   * nouvellement déposés, avec le type IMPOSÉ `piece_identite_cotitulaire`.
   *
   * Le type est imposé et non deviné : une CNI de co-titulaire est visuellement
   * identique à celle de l'acquéreur, seul le fait d'avoir été déposée dans
   * CETTE zone permet de connaître son rôle.
   *
   * Sans cette analyse, ces pièces ne produisaient aucune extraction : elles
   * étaient invisibles dans la review et aucune règle ne pouvait vérifier la
   * présence du recto et du verso (retour François 2026-07-20, EQ-598-TE).
   */
  const handleCoBuyerFilesChange = useCallback(
    (urls: string[]) => {
      // État lu au moment du dépôt (et non capturé en dépendance) : la session
      // in-flight est créée plus bas dans le composant et peut changer entre
      // deux dépôts. `getState()` évite à la fois le problème d'ordre de
      // déclaration et une dépendance obsolète.
      const state = useFormCGIAStore.getState();
      const previous = state.coBuyerFiles;
      setCoBuyerFiles(urls);
      // Seuls les fichiers AJOUTÉS sont analysés — une suppression ne doit pas
      // relancer d'OCR (ni en refacturer le coût) sur les fichiers restants.
      const added = urls.filter((u) => !previous.includes(u));
      if (added.length > 0) {
        ingestUploadedUrls(added, 'piece_identite_cotitulaire', {
          sessionId: state.activeInFlightId ?? undefined,
        });
      }
    },
    [setCoBuyerFiles, ingestUploadedUrls],
  );
  const { startUpload, patchUpload, cancelUpload, uploads: bgUploads } = useBackgroundUploads();
  const setActiveInFlightId = useFormCGIAStore((s) => s.setActiveInFlightId);
  const storeActiveInFlightId = useFormCGIAStore((s) => s.activeInFlightId);

  // In-flight upload courant : créé au 1er dépôt de fichiers, étendu si
  // l'agence en glisse d'autres ensuite, patché à chaque transition OCR.
  // Ref pour ne pas relancer un create à chaque re-render.
  const inFlightIdRef = useRef<string | null>(null);
  const lastSnapshotRef = useRef<{ uploaded: number; analyzed: number; total: number }>({
    uploaded: 0,
    analyzed: 0,
    total: 0,
  });

  const location = useLocation();

  // ── Mode édition : détection du query param `registrationId` ──
  // Si présent, on fetch le contexte complet (uploadedDocs déjà OK + openRequests
  // à redéposer + invoicingType) et on hydrate le store en sautant les étapes
  // 1+2 (plate, taxConfirm) pour aller direct à l'ingestion ciblée.
  const [searchParams] = useSearchParams();
  const registrationIdParam = searchParams.get('registrationId');
  const requestedRegistrationId = registrationIdParam ? Number(registrationIdParam) : null;
  const isEditMode = requestedRegistrationId !== null && !Number.isNaN(requestedRegistrationId);

  const editContextQuery = useQuery({
    queryKey: ['agency-edit-context', requestedRegistrationId],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<{ success: boolean; data: any }>(
        'get',
        `/api/cg/${requestedRegistrationId}/agency-edit-context`,
      );
      return response.data?.data;
    },
    enabled: isEditMode,
    staleTime: 30_000,
  });

  // Hydratation one-shot quand la query résout — on évite de re-hydrater si
  // l'utilisateur a déjà déposé des fichiers (= les openDocumentRequests du
  // store correspondent déjà à ce registrationId).
  const editHydratedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isEditMode || !editContextQuery.data) return;
    if (editHydratedRef.current === requestedRegistrationId) return;
    const ctx = editContextQuery.data;
    // Dérivation département : 2 chiffres en métropole, 3 chiffres pour les
    // DOM-TOM (97x = Guadeloupe/Martinique/Guyane/Réunion/Mayotte, 98x = COM).
    const cp = (ctx.registration?.AddressPostalCode ?? '') as string;
    const department = cp.startsWith('97') || cp.startsWith('98') ? cp.slice(0, 3) : cp.slice(0, 2);
    // Adapter shape backend ({firebaseUrls, ocrFields, confidence, extractionId})
    // vers shape store front (UploadedDocResult avec ocrResponse complet).
    // Sans ça, les 35 règles cross-check appelaient getFieldValue sur un doc
    // sans `ocrResponse` et crashaient toutes silencieusement (catch global du
    // moteur), produisant un faux "0 blocage / 0 attention" en mode édition.
    const adaptedUploadedDocs: Partial<Record<CgDocType, UploadedDocResult>> = Object.fromEntries(
      Object.entries(ctx.uploadedDocs ?? {}).map(([docType, raw]) => {
        const r = (raw ?? {}) as {
          firebaseUrls?: string[];
          ocrFields?: Record<string, unknown>;
          confidence?: number;
        };
        return [
          docType,
          {
            docType: docType as CgDocType,
            firebaseUrls: r.firebaseUrls ?? [],
            ocrResponse: {
              docTypeDetected: docType as CgDocType,
              docTypeMatch: true,
              docTypeMatchConfidence: typeof r.confidence === 'number' ? r.confidence : 1,
              unreadable: false,
              warnings: [],
              fields: (r.ocrFields ?? {}) as any,
            },
          } as UploadedDocResult,
        ];
      }),
    );
    hydrateV2FromAgencyEditContext({
      registrationId: ctx.registration?.RegistrationID ?? requestedRegistrationId!,
      regPlate: ctx.registration?.RegPlate ?? '',
      department,
      taxData: ctx.registration?.TaxDataEstim ?? null,
      invoicingType: ctx.invoicingType ?? 'One-time',
      nextStatusOnFulfill: ctx.nextStatusOnFulfill ?? 'awaiting_payment',
      uploadedDocs: adaptedUploadedDocs,
      extractedFields: {}, // Pas critique pour le mode édition — on ne ré-affiche pas la HeroCard cross-checks
      openRequests: (ctx.openRequests ?? []).map((r: any) => ({
        id: r.id,
        docType: r.doc_type,
        reason: r.reason,
        createdAt: r.created_at,
      })),
      fulfilledRequests: (ctx.fulfilledRequests ?? []).map((r: any) => ({
        id: r.id,
        docType: r.doc_type,
        fulfilledAt: r.fulfilled_at,
      })),
      parcours: null,
    });
    editHydratedRef.current = requestedRegistrationId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editContextQuery.data, requestedRegistrationId]);

  // Lance automatiquement la session au premier affichage — sauf en mode
  // édition (le store est déjà hydraté avec l'état cible).
  useEffect(() => {
    if (v2Step === 'intro' && !isEditMode) {
      startV2();
    }
  }, [v2Step, startV2, isEditMode]);

  // Edit context pour buildDocRows : injecte les statuts 'requested' et
  // 'validated_locked' dans la checklist quand on est en mode redepôt.
  const editRowContext: EditModeRowContext = useMemo(
    () => ({
      isEditMode,
      requestedDocTypes: new Set(openDocumentRequests.map((r) => r.docType)),
      requestReasonByDocType: openDocumentRequests.reduce<Record<string, string | null>>((acc, r) => {
        acc[r.docType] = r.reason;
        return acc;
      }, {}),
      validatedLockedDocTypes: new Set(validatedLockedDocTypes),
    }),
    [isEditMode, openDocumentRequests, validatedLockedDocTypes],
  );

  // Queue effective : DEFAULT_DOCUMENT_QUEUE (9 docs standards) + tout
  // docType demandé par GOD qui n'y est pas (ex: kbis, attestation_hebergement
  // — la modale "Demander des pièces complémentaires" permet ces types qui
  // sortent du parcours standard 9 pièces). Sans ça, un kbis demandé ne
  // s'affichait JAMAIS dans la checklist côté agence en mode édition.
  //
  // Mode hébergement (méthodologie François §2.3, cas DC-39685) : la dropzone
  // « Pièce d'identité de l'hébergeur » apparaît automatiquement dès qu'une
  // attestation_hebergement a été déposée — c'est l'attestation qui matérialise
  // un contexte d'hébergement, PAS la simple présence d'une 2e CNI rangée en
  // slot hébergeur (qui n'est qu'un overflow anti-écrasement, cf.
  // useAutoExtractQueue). Sinon, la dropzone reste cachée pour ne pas polluer
  // la checklist des dossiers standards (~98 % du flux).
  //
  // Acquéreur personne morale (société) — cas DC-39988 (SCEA LA FERME DE
  // PLACHAMP achète à AGVV). Détecté si la cession marque l'acheteur comme
  // personne morale, OU si un Kbis a été déposé, OU si un SIRET / raison
  // sociale acheteur a été lu. Conséquences sur la checklist (cf.
  // effectiveDocQueue + notRequiredDocTypes) :
  //   • permis_conduire : NON requis (une société n'a pas de permis)
  //   • justif_domicile : NON requis (le siège du Kbis fait foi de domicile)
  //   • kbis : requis (pièce d'identité + de domiciliation de la personne
  //     morale ; la CNI du gérant ne sert qu'à identifier le représentant
  //     légal — elle reste dans la checklist via piece_identite).
  // ⚠️ Détection partagée (validation/personneMorale.ts) avec le récap agence et
  // la grille de review GOD : les trois DOIVENT trancher pareil, sinon un dossier
  // sort proprement de la checklist mais se voit créer des demandes de pièces
  // impossibles à satisfaire côté backend (EH-214-VH).
  const acheteurEstPersonneMorale = useMemo<boolean>(() => {
    return isAcheteurPersonneMorale({
      flagCession: extractedFields.acheteur_est_personne_morale?.field.value,
      // `kbis` non routé = dossier antérieur au dédoublement, traité comme
      // acquéreur (convention historique). Un `kbis_vendeur` ne doit SURTOUT pas
      // compter ici : il ferait perdre à l'acquéreur son permis et son
      // justificatif de domicile.
      hasKbis: Boolean(uploadedDocs.kbis_acheteur) || Boolean(uploadedDocs.kbis),
      acheteurSiret: extractedFields.acheteur_siret?.field.value,
      acheteurRaisonSociale: extractedFields.acheteur_raison_sociale?.field.value,
    });
  }, [
    extractedFields.acheteur_est_personne_morale,
    extractedFields.acheteur_siret,
    extractedFields.acheteur_raison_sociale,
    uploadedDocs.kbis,
    uploadedDocs.kbis_acheteur,
  ]);

  // Vendeur personne morale : matérialise son PROPRE slot Kbis, sans rien retirer
  // à l'acquéreur. Sert à pré-remplir raison sociale et SIRET du vendeur dans la
  // modale « Soumettre le CTVO » ; le SIV n'exige pas cette pièce.
  const vendeurEstPersonneMorale = useMemo<boolean>(() => {
    return isVendeurPersonneMorale({
      flagCession: extractedFields.vendeur_est_personne_morale?.field.value,
      hasKbisVendeur: Boolean(uploadedDocs.kbis_vendeur),
      vendeurSiret: extractedFields.vendeur_siret?.field.value,
      vendeurRaisonSociale: extractedFields.vendeur_raison_sociale?.field.value,
      titulaireSiren: extractedFields.titulaire_siren?.field.value,
    });
  }, [
    extractedFields.vendeur_est_personne_morale,
    extractedFields.vendeur_siret,
    extractedFields.vendeur_raison_sociale,
    extractedFields.titulaire_siren,
    uploadedDocs.kbis_vendeur,
  ]);

  // Nombre de pièces encore en cours (upload/analyse). Défini AVANT effectiveDocQueue
  // car ce dernier « baisse le rideau » sur les slots dérivés de l'OCR tant que
  // l'analyse tourne (valeurs partielles → verdict transitoire à ne pas afficher).
  const activeJobsCount = useMemo(
    () =>
      Object.values(ingestionItems).filter(
        (it) => it.status === 'queued' || it.status === 'uploading' || it.status === 'analyzing',
      ).length,
    [ingestionItems],
  );

  // Flags co-titulaire — calculés AVANT la file : la déclaration d'un
  // co-titulaire acheteur matérialise ses lignes de checklist (recto/verso).
  const cotitulaireFlags = useMemo(
    () => deriveCotitulaireFlags(extractedFields, uploadedDocs),
    [extractedFields, uploadedDocs],
  );

  // Construction de la file (pure, testée dans buildEffectiveDocQueue.test.ts).
  // Cas conditionnels : demandes GOD (édition), pack hébergement, Récépissé DA
  // (Parcours A — détecté OCR, déposé, OU déclaré agence étape 1), acquéreur
  // personne morale, pack succession (déclaré agence étape 1), lignes
  // co-titulaire acheteur, et dissociation RECTO/VERSO des pièces d'identité.
  const effectiveDocQueue = useMemo<readonly CgDocType[]>(
    () =>
      buildEffectiveDocQueue({
        isEditMode,
        openRequestDocTypes: openDocumentRequests.map((r) => r.docType),
        hasAttestationHebergement: Boolean(uploadedDocs.attestation_hebergement),
        hasRecepisseDa: Boolean(uploadedDocs.recepisse_da),
        vendeurEstPro: extractedFields.vendeur_est_personne_morale?.field.value === true,
        vendeurEstPersonneMorale,
        // Titulaire CG particulier = aucun SIREN au C.1. Requis pour le Parcours A
        // (sinon le vendeur pro revend son propre véhicule = vente directe classique).
        cgTitulaireParticulier: !(
          extractedFields.titulaire_siren?.field.value && String(extractedFields.titulaire_siren.field.value).trim()
        ),
        // Rideau : tant que l'analyse tourne, on ne matérialise PAS le récépissé
        // depuis l'OCR (une cession lue avant la carte grise ferait apparaître un
        // faux « Parcours A » transitoire). Le verdict OCR n'est pris en compte
        // qu'une fois toutes les pièces analysées. Les branches déclaratives
        // (récépissé déposé / rachat pro déclaré) restent actives pendant l'analyse.
        analysisInProgress: activeJobsCount > 0,
        acheteurEstPersonneMorale,
        isSuccession,
        isProfessionalResale,
        hasCotitulaireAcheteur: cotitulaireFlags.acheteurActif,
        listPiTitulaireVerso: shouldListVersoRow(uploadedDocs.piece_identite, uploadedDocs.piece_identite_verso, 'cni'),
        listPermisTitulaireVerso: shouldListVersoRow(
          uploadedDocs.permis_conduire,
          uploadedDocs.permis_conduire_verso,
          'permis',
        ),
        listPiCotitulaireVerso: shouldListVersoRow(
          uploadedDocs.piece_identite_cotitulaire,
          uploadedDocs.piece_identite_cotitulaire_verso,
          'cni',
        ),
      }),
    [
      isEditMode,
      openDocumentRequests,
      uploadedDocs.attestation_hebergement,
      uploadedDocs.recepisse_da,
      uploadedDocs.piece_identite,
      uploadedDocs.piece_identite_verso,
      uploadedDocs.permis_conduire,
      uploadedDocs.permis_conduire_verso,
      uploadedDocs.piece_identite_cotitulaire,
      uploadedDocs.piece_identite_cotitulaire_verso,
      extractedFields.vendeur_est_personne_morale,
      extractedFields.titulaire_siren,
      activeJobsCount,
      acheteurEstPersonneMorale,
      vendeurEstPersonneMorale,
      isSuccession,
      isProfessionalResale,
      cotitulaireFlags.acheteurActif,
    ],
  );

  // CT non exigible si véhicule ≤ 4 ans (MEC MiSIV prioritaire, OCR en
  // fallback) — même logique que la règle
  // cross-check #7 et que le calcul des missingDocTypes du recap.
  const ctRequired = useMemo(
    () => isCtRequiredFromSession({ extractedFields, taxData: storedTaxData, now: new Date() }),
    [extractedFields, storedTaxData],
  );

  const permisPourIdentite = useMemo(
    () =>
      uploadedDocs.permis_conduire
        ? {
            fields: uploadedDocs.permis_conduire.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined,
            warnings: uploadedDocs.permis_conduire.ocrResponse?.warnings,
          }
        : undefined,
    [uploadedDocs.permis_conduire],
  );

  const notRequiredDocTypes = useMemo<ReadonlySet<CgDocType>>(() => {
    const set = new Set<CgDocType>();
    if (!ctRequired) set.add('controle_technique');
    // Acquéreur personne morale : permis et justificatif de domicile du
    // particulier ne s'appliquent pas (le Kbis identifie et domicilie la
    // société). Cf. acheteurEstPersonneMorale. Cas DC-39988.
    if (acheteurEstPersonneMorale) {
      PM_NOT_REQUIRED_DOC_TYPES.forEach((dt) => set.add(dt));
    }
    // Permis format carte au dossier : il vaut pièce d'identité pour tout le
    // monde (référentiel CTVO §6), on ne réclame pas de CNI en plus. Même
    // fonction que la règle cross-check, le récap et la grille de review
    // (GZ-923-ZV, retour François du 02/10/2026).
    if (permisDeposeVautPieceIdentite(permisPourIdentite)) {
      set.add('piece_identite');
    }
    return set;
  }, [ctRequired, acheteurEstPersonneMorale, permisPourIdentite]);

  // ── Co-titulaire : départage des CNI (les flags sont calculés plus haut,
  // avant la file effective). Le slot « Pièce d'identité » doit montrer la CNI
  // du titulaire (départage cession/mandat), pas la 1re CNI arrivée.
  const acquereurCni = useMemo(() => resolveAcquereurCni(uploadedDocs), [uploadedDocs]);
  // CNI du co-titulaire acquéreur détectée dans le premier lot (départage par
  // nom) — repli du slot zone dédiée pour les cross-checks (cf. plus bas).
  const cotitulairePiFromBatch = useMemo(() => resolveCotitulairePiFromBatch(uploadedDocs), [uploadedDocs]);
  // URL de la CNI du titulaire à forcer dans le slot principal (si un départage a
  // eu lieu : swap = la CNI acquéreur est dans le mauvais slot).
  const titulaireCniUrl = acquereurCni?.swap ? acquereurCni.acquereur.firebaseUrls?.[0] : undefined;

  const docRows = useMemo(
    () =>
      buildDocRows(
        effectiveDocQueue,
        ingestionItems,
        editRowContext,
        notRequiredDocTypes,
        uploadedDocs,
        titulaireCniUrl,
        identityAmbiguousUrls,
      ),
    [
      effectiveDocQueue,
      ingestionItems,
      editRowContext,
      notRequiredDocTypes,
      uploadedDocs,
      titulaireCniUrl,
      identityAmbiguousUrls,
    ],
  );

  // Toutes les pièces déposées non retenues comme représentant d'un slot :
  // échecs (à redéposer) ET pièces reçues en plus (verso, doublon, bien prises
  // en compte). Remontées dans le sous-bloc « Autres pièces reçues » de la
  // checklist pour qu'aucun fichier envoyé ne disparaisse de l'écran (Biwiz).
  const receivedUploadsRaw = useMemo(
    () => selectReceivedUploads(ingestionItems, titulaireCniUrl),
    [ingestionItems, titulaireCniUrl],
  );

  const toggleCotitulaireAcheteur = useCallback(
    // 'false' explicite (pas '') pour que le décochage prime sur l'auto-détection.
    (checked: boolean) => updateExtractedFieldValue('cobuyer_present', checked ? 'true' : 'false'),
    [updateExtractedFieldValue],
  );
  const toggleCotitulaireVendeur = useCallback(
    (checked: boolean) => updateExtractedFieldValue('vendeur_cotitulaire_present', checked ? 'true' : 'false'),
    [updateExtractedFieldValue],
  );

  // La CNI du co-titulaire (2e CNI) est rangée dans son sous-bloc dédié quand un
  // co-titulaire acheteur est déclaré → on la retire de « Autres pièces reçues »
  // (identifiée par son URL Firebase).
  const cotitulaireCniUrl = cotitulaireFlags.acheteurActif
    ? acquereurCni?.cotitulaire?.firebaseUrls?.[0]
    : undefined;
  const cotitulaireFileName = cotitulaireCniUrl
    ? Object.values(ingestionItems).find((it) => it.firebaseUrl === cotitulaireCniUrl)?.fileName
    : undefined;
  const receivedUploads = useMemo(
    () =>
      cotitulaireFileName
        ? receivedUploadsRaw.filter((u) => u.fileName !== cotitulaireFileName)
        : receivedUploadsRaw,
    [receivedUploadsRaw, cotitulaireFileName],
  );

  // ── Mode édition : fulfill des requests redéposées ──
  const navigate = useNavigate();
  const [fulfillSubmitting, setFulfillSubmitting] = useState(false);
  const [fulfillError, setFulfillError] = useState<string | null>(null);

  // DocTypes uniques demandés (pour l'UX, déduplication des doublons god).
  // Si god a demandé 2x le même type (ex: kbis), c'est UNE case "Kbis" pour
  // l'agence — un seul fichier suffit logiquement. Le fulfill backend ferme
  // ensuite toutes les requests de ce docType.
  const openRequestedDocTypes = useMemo(() => {
    const set = new Set<CgDocType>();
    openDocumentRequests.forEach((r) => set.add(r.docType));
    return set;
  }, [openDocumentRequests]);
  const openRequestedDocTypeCount = openRequestedDocTypes.size;

  // Compte des pièces redéposées qui matchent une openRequest (= prêtes à fulfill).
  // On considère une pièce comme "redéposée" si on a un ingestionItem en status='ok'
  // dont docTypeDetected matche une openRequest. Compté par docType unique.
  const readyToFulfillCount = useMemo(() => {
    if (!isEditMode) return 0;
    const readyDocTypes = new Set<CgDocType>();
    Object.values(ingestionItems).forEach((it) => {
      if (it.status === 'ok' && it.docTypeDetected && it.docTypeDetected !== 'unknown') {
        if (openRequestedDocTypes.has(it.docTypeDetected as CgDocType)) {
          readyDocTypes.add(it.docTypeDetected as CgDocType);
        }
      }
    });
    return readyDocTypes.size;
  }, [isEditMode, ingestionItems, openRequestedDocTypes]);

  /**
   * Le bouton s'ouvre dès qu'UNE pièce demandée est redéposée, plus seulement
   * quand elles le sont toutes.
   *
   * L'exigence du tout-ou-rien enfermait les dossiers : une agence qui ne
   * possède qu'une des deux pièces réclamées ne pouvait rien renvoyer, et une
   * demande de type « autre » (créée par un refus GOD, qu'aucune détection OCR
   * ne peut satisfaire) rendait le bouton définitivement gris. Au 22/08/2026,
   * 15 dossiers attendaient ainsi, le plus ancien depuis le 13/07.
   *
   * `fulfillOpenRequests` ne solde de toute façon que les demandes réellement
   * couvertes par un dépôt : un envoi partiel est sans risque, le dossier reste
   * en attente pour ce qui manque encore.
   */
  const someRequestsReady = isEditMode && openRequestedDocTypeCount > 0 && readyToFulfillCount > 0;
  const allRequestsReady = someRequestsReady && readyToFulfillCount === openRequestedDocTypeCount;

  const fulfillOpenRequests = async () => {
    setFulfillError(null);
    setFulfillSubmitting(true);
    try {
      // Mapping docType → openRequest.id pour itérer
      const requestsToFulfill = openDocumentRequests
        .map((req) => {
          // Cherche l'ingestionItem ok qui correspond
          const item = Object.values(ingestionItems).find(
            (it) => it.status === 'ok' && it.docTypeDetected === req.docType && it.firebaseUrl,
          );
          return item ? { requestId: req.id, url: item.firebaseUrl as string } : null;
        })
        .filter((x): x is { requestId: number; url: string } => x !== null);

      if (requestsToFulfill.length === 0) {
        throw new Error('Aucune pièce redéposée à transmettre');
      }

      // Fulfill séquentiel — le backend gère la transition de statut sur le
      // dernier fulfill (cf. cgReviewController.fulfillDocumentRequestController).
      let finalNewStatus: string | null = null;
      for (const { requestId, url } of requestsToFulfill) {
        const response = await makeAuthenticatedApiCall<{
          success: boolean;
          newStatus: string | null;
          error?: string;
        }>('post', `/api/cg/document-requests/${requestId}/fulfill`, { fulfilledUrl: url });
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Échec du fulfill');
        }
        if (response.data.newStatus) finalNewStatus = response.data.newStatus;
      }

      // Toast + redirect vers /list-cg
      const toastLabel =
        finalNewStatus === 'awaiting_payment'
          ? 'Pièces renvoyées — votre dossier passe en attente de paiement.'
          : 'Pièces renvoyées — Skipcar va relire votre dossier.';
      // On reset le store pour libérer la session
      resetV2();
      navigate('/list-cg', { state: { toast: toastLabel } });
    } catch (err: any) {
      setFulfillError(err?.message || 'Une erreur est survenue pendant le renvoi des pièces.');
    } finally {
      setFulfillSubmitting(false);
    }
  };

  const completedCount = docRows.filter((r) => r.status === 'ok').length;
  // Les pièces non exigibles (CT véhicule ≤ 4 ans) sortent du total attendu :
  // le header affiche "8/8" et le footer ne réclame plus une pièce inexistante.
  const totalCount = docRows.filter((r) => r.status !== 'not_required').length;

  // Nombre de jobs en cours (upload + analyse) — pour l'indicateur animé.
  const loadingMessage = useRotatingLoadingMessage(activeJobsCount > 0);

  const findingsReport = useMemo(
    () =>
      runCrossChecks(
        ALL_CROSS_CHECK_RULES,
        {
          // Repli co-titulaire : une CNI co-titulaire déposée dans le PREMIER
          // LOT (dropzone principale) est classée `piece_identite` et rangée
          // dans un slot CNI générique — le slot `piece_identite_cotitulaire`
          // (type imposé par la zone dédiée) reste vide. On l'y injecte pour le
          // SEUL contexte des règles quand le départage par nom l'identifie,
          // sinon piece_identite_cotitulaire_incomplete criait « aucune pièce
          // déposée » pendant que la checklist affichait « Rattachée au
          // co-titulaire » (re-test EQ-598-TE). La zone dédiée reste
          // prioritaire ; le routage des slots/champs n'est pas modifié.
          docs:
            !uploadedDocs.piece_identite_cotitulaire && cotitulairePiFromBatch
              ? { ...uploadedDocs, piece_identite_cotitulaire: cotitulairePiFromBatch }
              : uploadedDocs,
          now: new Date(),
          // Permet à la règle coherence_immat_user_vs_pieces de comparer
          // la plaque/dép saisis à l'étape 1 avec les OCR des pièces.
          userInput: { regPlate: storedRegPlate, department: storedDepartment },
          // MiSIV est la source de vérité externe pour VIN / dateMEC.
          // Permet aux règles vin_misiv_mismatch et mec_misiv_mismatch
          // de détecter une CG qui n'appartient pas au véhicule recherché.
          taxData: storedTaxData,
          // Case « co-titulaire vendeur » cochée à la main → la règle
          // signature_cession_cotitulaire réclame la vérif de sa signature même
          // si l'OCR de la carte grise n'a pas lu le C.4.1.
          cotitulaireVendeurManuel: cotitulaireFlags.vendeurActif,
          // Pièces du co-titulaire ACQUÉREUR : uploadées sans OCR, donc absentes
          // de `docs`. Seul leur nombre est exploitable — il alimente la règle
          // piece_identite_cotitulaire_incomplete (réclame recto ET verso).
          cotitulaireAcheteurFilesCount: coBuyerFiles.length,
        },
        // Trace IA console : chaque règle évaluée est loggée pour que le
        // raisonnement complet soit copiable vers un Claude externe.
        { trace: true },
      ),
    [
      uploadedDocs,
      cotitulairePiFromBatch,
      storedRegPlate,
      storedDepartment,
      storedTaxData,
      cotitulaireFlags.vendeurActif,
      coBuyerFiles.length,
    ],
  );
  const allFindings = useMemo(
    () => [...findingsReport.errors, ...findingsReport.warnings, ...findingsReport.infos],
    [findingsReport],
  );

  // ── Voie B : verdict de l'audit batch Opus comme source de vérité ──
  // Le preflight Opus (déclenché plus bas, dès la fin des analyses OCR) voit
  // toutes les pièces ensemble avec le référentiel CTVO complet — c'est lui
  // qui égale l'analyse de référence. On capture son résultat ici et il
  // PILOTE la colonne d'alertes. Tant qu'il n'est pas arrivé (analyses en
  // cours, ou échec réseau), on retombe sur le moteur regex rapide.
  const [opusVerdict, setOpusVerdict] = useState<AuditVerdict | null>(null);
  const [opusResult, setOpusResult] = useState<AuditBatchResult | null>(null);

  const opusFindings = useMemo(() => auditResultToFindings(opusResult), [opusResult]);
  const opusAvailable = opusResult !== null && opusVerdict !== null && opusVerdict !== 'ERROR';
  /** `transaction.parcours` brut de l'audit batch (null si absent). */
  const opusParcoursRaw =
    (opusResult as { transaction?: { parcours?: string } } | null)?.transaction?.parcours ?? null;

  // ── Voie C (SPEC François) : moteur d'analyse CTVO v1 ──
  // Quand le flag form_cgia est ON, on appelle le nouveau moteur
  // un-seul-appel Claude multimodal. Son verdict est PRIORITAIRE sur Opus,
  // qui devient lui-même un fallback transitoire. Pas de flag dédié au
  // moteur v1 : c'est la même fonctionnalité que FormCGIAv2 — l'agence
  // qui a accès au nouveau formulaire a aussi le nouveau moteur.
  //
  // Le hook bascule automatiquement entre :
  //  - mode preflight (sans registrationId — flow initial agence) : POST
  //    /api/cg/analyze-v1/preflight, persistance par images_hash, adoption
  //    automatique à la création de la CG (pas de re-coût Claude).
  //  - mode post-soumission (registrationId présent — édition / review) :
  //    POST /api/cg/:id/analyze-v1.
  const { enabled: specV1FlagOn } = useAgencyFeatureFlag('form_cgia');
  const v1RegistrationId = requestedRegistrationId ?? editingRegistrationId ?? null;
  const v1ImageUrls = useMemo(
    () =>
      Object.values(uploadedDocs)
        .flatMap((doc): string[] => doc?.firebaseUrls ?? [])
        .filter((u): u is string => typeof u === 'string' && u.length > 0),
    [uploadedDocs],
  );
  // v1 ne se déclenche qu'à l'étape récap ou pendant la soumission. Avant
  // (étape `ingesting`), on relançait l'analyse à CHAQUE pièce déposée — soit
  // 10–12 appels Opus en série pour un dossier complet, chacun consommant
  // ~60–90s et beaucoup de tokens pour rien puisque seul le dernier verdict
  // (set d'images complet) est exploité. À partir de `recap`, le set est
  // figé : un seul appel suffit. Si l'utilisateur clique « Soumettre » très
  // vite, on retombera sur l'audit post-création (pas de régression UX).
  const v1Active = specV1FlagOn && v1ImageUrls.length > 0 && (v2Step === 'recap' || v2Step === 'submitting');
  const {
    result: v1Result,
    statut: v1Statut,
    isLoading: v1IsLoading,
  } = useCtvoAnalyzeV1({
    registrationId: v1RegistrationId,
    imageUrls: v1ImageUrls,
    enabled: v1Active,
  });

  const v1Findings = useMemo(() => ctvoV1ResultToFindings(v1Result), [v1Result]);
  const v1Available = v1Active && v1Result !== null && v1Statut !== null && v1Statut !== 'analyse_echouee';

  // Contradiction « succession déclarée à l'étape 1 » vs verdict des moteurs.
  // Cas GD-567-EJ (#13374) : case cochée, analyse « classique », aucune pièce
  // de succession déposée → le dossier réclamait 2 pièces fantômes sans que
  // personne ne soit alerté de l'écart. L'agence voit l'alerte AVANT de
  // soumettre, donc peut se corriger seule (cf. bandeau parcours, bouton
  // « Corriger la déclaration »).
  const successionMismatchFinding = useMemo(
    () =>
      buildSuccessionDeclarationMismatchFinding({
        isSuccession,
        v1Parcours: v1Available && v1Result ? v1Result.dossier.parcours : null,
        opusParcours: opusAvailable ? opusParcoursRaw : null,
        hasSuccessionDoc: Boolean(uploadedDocs.acte_deces || uploadedDocs.justif_succession),
      }),
    [
      isSuccession,
      v1Available,
      v1Result,
      opusAvailable,
      opusParcoursRaw,
      uploadedDocs.acte_deces,
      uploadedDocs.justif_succession,
    ],
  );

  // Union des 3 moteurs (regex frontend + audit batch Opus + CTVO v1
  // Claude) — pas une cascade exclusive. Auparavant, dès qu'une voie LLM
  // était disponible, on masquait les findings regex : cohérent en théorie
  // (« le LLM voit plus large »), mais en pratique les LLM ratent des
  // contrôles déterministes simples (ex. ct_validite : la règle regex
  // mesure exactement « (now - date_ct) > seuil mois » avec un seuil qui
  // dépend du résultat, le LLM peut se tromper ou oublier). Résultat
  // observé : alerte CT défaillance majeure visible sur /cg/:id/review
  // (regex pur) mais ABSENTE de /form-cgia (cascade qui masquait le regex
  // au profit du LLM). Le filet de sécurité regex doit toujours être en
  // surface. Dédup par ruleId (les préfixes ctvo_v1_*, opus_*, et brut
  // pour regex garantissent qu'aucune collision n'est silencieuse).
  // Union dédupliquée des 3 moteurs, AVANT tout filtre d'affichage. C'est CE
  // jeu de findings (le résultat d'analyse global brut) qui est figé dans le
  // snapshot persisté et rejoué tel quel par la page review → iso garantie.
  const mergedFindings = useMemo(() => {
    const seen = new Set<string>();
    const merged: CrossCheckFinding[] = [];
    const pushUnique = (list: CrossCheckFinding[]) => {
      list.forEach((f) => {
        if (seen.has(f.ruleId)) return;
        seen.add(f.ruleId);
        merged.push(f);
      });
    };
    if (v1Available) pushUnique(v1Findings);
    if (opusAvailable) pushUnique(opusFindings);
    pushUnique(allFindings);
    if (successionMismatchFinding) pushUnique([successionMismatchFinding]);
    return merged;
  }, [v1Available, v1Findings, opusAvailable, opusFindings, allFindings, successionMismatchFinding]);

  // Moteur ayant piloté le verdict (trace + snapshot). v1 → Opus → regex.
  const analysisEngine: 'v1' | 'opus' | 'regex' = v1Available ? 'v1' : opusAvailable ? 'opus' : 'regex';

  // Filtre d'affichage partagé avec la review (cf. v2/findingsDisplay) — c'est
  // CE filtre, appliqué des deux côtés sur la même union brute, qui garantit
  // l'iso du compteur "X majeures · Y mineures" entre /form-cgia et la review.
  const effectiveFindings = useMemo(() => filterFindingsForDisplay(mergedFindings, { isGod }), [mergedFindings, isGod]);
  const alertsCount = effectiveFindings.filter((f) => f.severity === 'error' || f.severity === 'warning').length;
  // Distinction majeures / mineures pour le bandeau : un ton FERME (« à
  // corriger ») dès qu'une majeure existe, le ton rassurant (« à vérifier /
  // continuez sereinement ») n'étant réservé qu'aux dossiers sans majeure.
  const majorAlertsCount = effectiveFindings.filter((f) => f.severity === 'error').length;
  const minorAlertsCount = effectiveFindings.filter((f) => f.severity === 'warning').length;
  const hasMajorAlerts = majorAlertsCount > 0;
  const hasBlockingFromOpus = opusAvailable && opusVerdict === 'KO';
  const hasBlockingFromV1 = v1Available && v1Statut === 'bloque';

  // Détection du parcours métier à partir des findings : si une règle
  // Parcours A s'est déclenchée (récépissé requis/manquant/chronologie) OU
  // si une pièce recepisse_da a été déposée, on bascule la bannière en
  // "Parcours A". Branche la bannière sur la réalité du dossier au lieu du
  // libellé figé "non activés".
  const setParcours = useFormCGIAStore((s) => s.setParcours);
  const setAnalysisSnapshot = useFormCGIAStore((s) => s.setAnalysisSnapshot);
  const detected = useMemo(() => {
    const parcoursAFindingIds = ['parcours_a_requis', 'recepisse_da_manquant_parcours_a', 'chronologie_recepisse_da'];
    return detectParcours({
      isSuccession,
      isProfessionalResale,
      v1Label: v1Available ? ctvoV1ParcoursToBannerLabel(v1Result) : null,
      opusParcours: opusAvailable ? opusParcoursRaw : null,
      hasParcoursAFinding: allFindings.some((f) => parcoursAFindingIds.includes(f.ruleId)),
      hasRecepisseDa: Boolean(uploadedDocs.recepisse_da),
    });
  }, [
    isSuccession,
    isProfessionalResale,
    v1Available,
    v1Result,
    opusAvailable,
    opusParcoursRaw,
    allFindings,
    uploadedDocs.recepisse_da,
  ]);
  const detectedParcours = detected.label;
  const detectedParcoursSource = detected.source;

  useEffect(() => {
    setParcours(detectedParcours);
  }, [detectedParcours, setParcours]);

  // « Corriger la déclaration » : renvoie à l'étape 1 pour décocher (ou cocher)
  // « succession » / « rachat pro ». Sans ce retour, une case cochée par erreur
  // était IRRÉVERSIBLE — le seul recours était de recommencer tout le dossier,
  // ce qui a laissé passer GD-567-EJ (#13374) et EB-700-HD (#13377) en
  // succession. Les pièces déjà déposées et l'analyse en cours sont conservées :
  // on ne touche qu'au pas de navigation. `taxConfirmReturn` est remis à
  // `ingesting` pour que la validation du tarif rebascule bien sur le dépôt.
  const handleCorrectDeclaration = () => {
    setTaxConfirmReturn('ingesting');
    setV2Step('plate_and_dept');
  };

  // Fige le résultat d'analyse global dans le store (envoyé avec le POST
  // /api/cg à la soumission, cf. RecapAgencyScreen). On persiste l'union
  // BRUTE non filtrée (`mergedFindings`) : la review (god) y retrouve tout, y
  // compris les INFO de diagnostic ; le calcul des compteurs blocage/attention
  // ne dépend que des error/warning, donc reste iso avec l'affichage agence.
  useEffect(() => {
    // On ne fige le snapshot que lorsque l'analyse est TERMINÉE (activeJobsCount === 0).
    // Pendant l'upload, `detectedParcours` peut basculer transitoirement (ex. « Parcours A »
    // du fallback regex avant que v1/Opus résolvent vers « classique ») : persister un état
    // intermédiaire ferait afficher un parcours erroné dans la review. On capture donc le
    // parcours / le verdict FINAL — « on baisse le rideau à la fin » (cf. bloc Alertes).
    if (activeJobsCount > 0) return;
    setAnalysisSnapshot({
      findings: mergedFindings.map((f) => ({
        ruleId: f.ruleId,
        severity: f.severity,
        message: f.message,
        documentTypes: f.documentTypes,
        fields: f.fields,
      })),
      parcoursLabel: detectedParcours,
      // Provenance du parcours : sans elle, la review affiche « Succession »
      // sans pouvoir dire si c'est une DÉCLARATION de l'agence (case cochée à
      // l'étape 1) ou une DÉTECTION de l'analyse. Cf. GD-567-EJ (#13374).
      parcoursSource: detectedParcoursSource,
      engine: analysisEngine,
    });
  }, [mergedFindings, detectedParcours, detectedParcoursSource, analysisEngine, activeJobsCount, setAnalysisSnapshot]);

  // Immat + résumé véhicule pour le Hero : priorité à la valeur saisie
  // à l'étape 1, fallback sur l'OCR si jamais l'agence n'est pas encore
  // passée par PlateAndDepartmentScreen (cas de session reprise).
  const regPlate =
    storedRegPlate ||
    (typeof extractedFields.immatriculation?.field.value === 'string'
      ? (extractedFields.immatriculation?.field.value as string)
      : null);
  const vehicleSummary = (() => {
    const marque = extractedFields.marque?.field.value;
    const modele = extractedFields.modele?.field.value;
    const parts = [marque, modele].filter((v) => typeof v === 'string' && v);
    return parts.length > 0 ? parts.join(' · ') : null;
  })();

  // Score global : moyenne des confidences des champs extraits (0-100).
  const aiConfidence = useMemo(() => {
    const confidences = Object.values(extractedFields).map((e) => e.field.confidence);
    if (confidences.length === 0) return null;
    const avg = confidences.reduce((sum, c) => sum + c, 0) / confidences.length;
    return Math.round(avg * 100);
  }, [extractedFields]);

  const handleFilesAdded = async (files: File[]) => {
    const now = Date.now();
    const filesById: Record<string, File> = {};
    const items: IngestionItem[] = files.map((file) => {
      const id = makeIngestionItemId(file);
      filesById[id] = file;
      return {
        id,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        status: 'queued',
        createdAt: now,
      };
    });
    enqueueIngestionItems(items);

    // L'inFlightId courant peut venir de 3 sources :
    //  1. inFlightIdRef.current (session démarrée dans ce mount FormCGIAv2)
    //  2. storeActiveInFlightId (session restaurée via "Basculer" depuis le tracker)
    //  3. Aucun → on en crée un nouveau.
    let activeId: string | null = inFlightIdRef.current ?? storeActiveInFlightId ?? null;

    // Sécurité : si l'id stocké n'existe plus en local (row purgée, expirée,
    // ou annulée hors de ce composant), on force la création d'un nouveau.
    // Sinon on patcherait un id fantôme → 404 silencieux → toast jamais visible.
    if (activeId && !bgUploads.some((u) => u.id === activeId)) {
      inFlightIdRef.current = null;
      setActiveInFlightId(null);
      activeId = null;
    }

    if (!activeId) {
      const result = await startUpload({
        filesTotal: items.length,
        payload: {
          regPlate: useFormCGIAStore.getState().regPlate,
          department: useFormCGIAStore.getState().department,
          v2Step: useFormCGIAStore.getState().v2Step,
        },
      });
      if (result) {
        activeId = result.row.id;
        inFlightIdRef.current = activeId;
        setActiveInFlightId(activeId);
      }
    } else if (!inFlightIdRef.current) {
      // Session restaurée via le store mais ref locale vide → resynchronise.
      inFlightIdRef.current = activeId;
    }

    // Capture du contexte de session : inFlightId + valeurs de l'étape 1
    // (regPlate, dept, taxData) au moment du dépôt. Embarquées dans la
    // closure du callback onProgress → garantit que les patches PATCH
    // toujours la BONNE row avec un payload cohérent, même si l'utilisateur
    // bascule sur une autre session entre-temps.
    const capturedInFlightId = activeId;
    const stateAtDrop = useFormCGIAStore.getState();
    const initialContext = {
      regPlate: stateAtDrop.regPlate,
      department: stateAtDrop.department,
      taxData: stateAtDrop.taxData,
      v2Step: stateAtDrop.v2Step,
      parcours: stateAtDrop.parcours,
    };
    startIngestion(items, filesById, {
      sessionId: capturedInFlightId ?? undefined,
      onProgress: capturedInFlightId
        ? ({ counters, snapshot }) => {
            const allDone = counters.total > 0 && counters.analyzed === counters.total;
            // Payload backend = snapshot complet de la session — permet à
            // "Basculer sur ce dossier" de rehydrater un état frais et ISO
            // avec les compteurs du toast.
            void patchUpload(capturedInFlightId, {
              // Le total peut avoir grandi : un PDF multi-documents est
              // remplacé par ses parties pendant l'analyse (skip-446).
              files_total: counters.total,
              files_uploaded: counters.uploaded,
              files_analyzed: counters.analyzed,
              state: allDone ? 'analyzing' : 'uploading',
              payload: {
                ...initialContext,
                ingestionItems: snapshot.ingestionItems,
                uploadedDocs: snapshot.uploadedDocs,
                extractedFields: snapshot.extractedFields,
                // Sans le pool, une reprise de ce dossier repart avec un pool
                // vide et `applyIdentityRouting` efface ses pièces d'identité
                // au premier résultat OCR suivant (cf. handleSeeUpload).
                identityDocsPool: snapshot.identityDocsPool,
              },
            } as any);
          }
        : undefined,
    });
  };

  // Si le store est resetV2() — soit via "Recommencer", soit via "Nouvelle
  // CG" depuis le tracker — on libère la ref pour qu'un futur handleFilesAdded
  // crée une NOUVELLE in-flight row. Les processItem en cours continuent
  // de patcher leur ANCIEN in-flight via la closure capturée.
  useEffect(() => {
    if (v2Step === 'plate_and_dept' && Object.keys(ingestionItems).length === 0) {
      inFlightIdRef.current = null;
      lastSnapshotRef.current = { uploaded: 0, analyzed: 0, total: 0 };
    }
  }, [v2Step, ingestionItems]);

  // Toast pédagogique T+5s : 5 secondes après le 1er fichier déposé, on
  // signale à l'agence qu'elle peut continuer en arrière-plan. Affiché une
  // seule fois par session (ref locale `bgHintShownRef`).
  const [bgHintOpen, setBgHintOpen] = useState<boolean>(false);
  const bgHintShownRef = useRef<boolean>(false);
  useEffect(() => {
    if (bgHintShownRef.current) return;
    const hasJobs = Object.values(ingestionItems).some(
      (it) => it.status === 'queued' || it.status === 'uploading' || it.status === 'analyzing',
    );
    if (!hasJobs) return;
    const timer = setTimeout(() => {
      if (!bgHintShownRef.current) {
        bgHintShownRef.current = true;
        setBgHintOpen(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [ingestionItems]);

  // ── Audit batch preflight ──────────────────────────────────
  // Pain point : l'audit Opus (mode "Claude Desktop") ne démarrait qu'à la
  // soumission. On le déclenche DÈS que toutes les analyses du batch sont
  // terminées — à la soumission, la registration ADOPTE le verdict déjà
  // calculé (un seul appel Opus payé, verdict prêt pour la review GOD).
  const preflightTriggeredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode) return;
    if (activeJobsCount > 0) return; // analyses encore en cours
    // Ne pas déclencher pendant `ingesting` : l'agence est encore en train de
    // déposer des pièces, chaque nouveau dépôt invalide le verdict. On attend
    // l'étape `recap` (set d'images figé) pour ne payer qu'un seul appel
    // Anthropic. Si on déclenchait à `ingesting`, chaque upload relançait un
    // audit + un CTVO v1 → ~24 appels Claude payés pour 1 dossier de 12
    // pièces, alors qu'un seul comptait à la fin.
    if (v2Step !== 'recap' && v2Step !== 'submitting') return;
    const analyzedUrls = Object.values(ingestionItems)
      .filter(
        (it) =>
          (it.status === 'ok' || it.status === 'unreadable' || it.status === 'mismatch') &&
          typeof it.firebaseUrl === 'string' &&
          it.firebaseUrl.length > 0,
      )
      .map((it) => it.firebaseUrl as string)
      .sort();
    if (analyzedUrls.length === 0) return;
    const batchKey = analyzedUrls.join('\n');
    if (preflightTriggeredKeyRef.current === batchKey) return; // déjà lancé pour ce set
    preflightTriggeredKeyRef.current = batchKey;
    // Nouveau set d'images → le verdict Opus précédent est périmé : on le
    // vide le temps que le nouvel audit revienne (la colonne retombe sur le
    // filet regex pendant ce court intervalle).
    setOpusVerdict(null);
    setOpusResult(null);

    // Rapport global consolidé en console : fichiers, pièces, champs
    // fusionnés, cross-checks — LE bloc à copier pour un second avis Claude.
    logGlobalIaReport({
      regPlate: storedRegPlate || null,
      department: storedDepartment || null,
      ingestionItems,
      uploadedDocs,
      extractedFields,
      findings: findingsReport,
    });

    traceIa(
      'AUDIT-OPUS',
      `Toutes les analyses du batch sont terminées (${analyzedUrls.length} pièce(s)). Déclenchement de l'audit global préflight: ` +
        `POST /api/cg/audit-batch/preflight. Le backend envoie TOUTES les images du dossier + les extractions OCR pré-calculées ` +
        `à Claude Opus (claude-opus-4-7, SDK @anthropic-ai/sdk) avec le référentiel CTVO Skipcar complet en system prompt ` +
        `(mode "Claude Desktop") : analyse croisée multi-pièces, verdict OK / A_VERIFIER / KO. ` +
        `Idempotent par set d'images — un set déjà audité renvoie le verdict en cache.`,
    );
    void (async () => {
      try {
        const response = await makeAuthenticatedApiCall<CgAuditBatchApiResponse>(
          'post',
          '/api/cg/audit-batch/preflight',
          { imageUrls: analyzedUrls },
        );
        // Garde anti-course : pendant l'appel, le set de pièces a pu changer
        // (ex : CNI co-titulaire déposée après un premier passage au récap) →
        // un nouveau préflight est parti pour le set élargi. Ce verdict-ci est
        // périmé : l'appliquer écraserait le verdict frais si sa réponse est
        // arrivée avant la nôtre.
        if (preflightTriggeredKeyRef.current !== batchKey) {
          traceIa('AUDIT-OPUS', 'Réponse d’un préflight périmé (le set de pièces a changé depuis) — verdict ignoré.');
          return;
        }
        const audit = response.data?.data;
        if (!response.data?.success || !audit) {
          traceIa('AUDIT-OPUS', `Préflight sans verdict exploitable: ${JSON.stringify(response.data)}`);
          return;
        }
        traceIa(
          'AUDIT-OPUS',
          `Verdict de l'audit: ${audit.verdict} — modèle: ${audit.modelUsed}` +
            `${audit.cached ? ' (résultat servi depuis le cache, aucun nouvel appel Opus)' : ''} — ` +
            `tokens: ${audit.promptTokens ?? '?'} entrée / ${audit.completionTokens ?? '?'} sortie.`,
        );
        // Voie B : on pousse le verdict + le détail structuré dans le state
        // pour que la colonne d'alertes agence reflète l'analyse Opus
        // (source de vérité) plutôt que le seul moteur regex.
        setOpusVerdict((audit.verdict as AuditVerdict) ?? null);
        setOpusResult((audit.result as AuditBatchResult) ?? null);
        if (audit.markdown) {
          traceIa(
            'AUDIT-OPUS',
            `Rapport d'audit complet rédigé par Claude Opus (raisonnement multi-pièces, à copier tel quel) :\n${audit.markdown}`,
          );
        }
        traceIa(
          'AUDIT-OPUS',
          "Résultat structuré complet (verdict, pièces, blocages, points d'attention) :",
          audit.result,
        );
      } catch (err: any) {
        console.warn('audit-batch preflight failed (non-blocking):', err);
        traceIa(
          'AUDIT-OPUS',
          `Échec du préflight (non bloquant — l'audit sera relancé à la soumission): ${err?.response?.data?.error ?? err?.message ?? 'erreur inconnue'}`,
        );
      }
    })();
  }, [
    isEditMode,
    activeJobsCount,
    v2Step,
    ingestionItems,
    uploadedDocs,
    extractedFields,
    findingsReport,
    storedRegPlate,
    storedDepartment,
  ]);

  // Seuil assoupli : on laisse passer dès 6 pièces reçues (sur 9 attendues).
  // Les alertes (majeures ou mineures) ne sont PAS bloquantes côté agence :
  // Skipcar les traitera dans le dashboard GOD review. On veut que l'agence
  // puisse soumettre sans friction dès qu'elle a le quota de pièces.
  const MIN_DOCS_TO_CONTINUE = 6;
  const canContinue = completedCount >= MIN_DOCS_TO_CONTINUE;

  // État du footer sticky (mode normal) — la soumission étant permise sur
  // dossier incomplet, le footer doit devenir proéminent dès que le dossier
  // est SOUMETTABLE, pas seulement quand il est complet :
  //  - 'collecting'  : quota non atteint OU analyses IA encore en cours → discret
  //  - 'submittable' : quota atteint, pièces manquantes → barre ambre + gros CTA
  //  - 'complete'    : toutes les pièces attendues validées → barre verte + gros CTA
  const submitFooterState: 'collecting' | 'submittable' | 'complete' = (() => {
    if (!canContinue || activeJobsCount > 0) return 'collecting';
    // Tant que la v1 est en cours d'analyse côté serveur, on n'affiche pas
    // encore "complete" : on attend son verdict pour éviter de promettre une
    // soumission qu'elle pourrait bloquer.
    if (v1Active && v1IsLoading) return 'submittable';
    // Voie C (v1) ou Voie B (Opus) : un verdict KO (blocage ANTS rédhibitoire)
    // empêche l'affichage "complete" même si toutes les pièces sont là — on
    // reste "submittable" (barre ambre) pour signaler le blocage à traiter.
    if (hasBlockingFromV1 || hasBlockingFromOpus) return 'submittable';
    return completedCount >= totalCount ? 'complete' : 'submittable';
  })();

  // Pièces réellement absentes (à nommer dans le message ambre). Les statuts
  // 'verifier'/'alerte' sont des pièces reçues — elles bloquent le passage à
  // 'complete' mais ne sont pas "manquantes".
  const missingDocTitles = docRows.filter((r) => r.status === 'pending' || r.status === 'reupload').map((r) => r.title);

  // Reset complet : vide la session Zustand (docs uploadés, items d'ingestion,
  // extractedFields, parcours) et revient à l'étape d'ingestion. On demande
  // confirmation si l'utilisateur a déjà commencé à déposer des fichiers,
  // sinon on reset sans friction.
  const hasWorkInProgress =
    Object.keys(ingestionItems).length > 0 || Object.keys(uploadedDocs).length > 0 || v2Step === 'recap';

  const handleResetForm = () => {
    if (hasWorkInProgress) {
      const confirmed = window.confirm(
        'Tous les documents déposés et les informations extraites seront perdus. Continuer ?',
      );
      if (!confirmed) return;
    }
    // Annule l'in-flight en arrière-plan s'il existe — le tracker disparaît
    // et la row passe en `cancelled` côté backend (audit trail conservé).
    // IMPORTANT : on annule AUSSI la file d'ingestion frontend (uploads/OCR
    // détachés), sinon ses promesses encore en vol réécrivent dans le store
    // wipé et repolluent la nouvelle session (« on ne peut plus recommencer »).
    const activeSessionId = inFlightIdRef.current ?? storeActiveInFlightId;
    if (activeSessionId) {
      abortIngestionSession(activeSessionId);
      void cancelUpload(activeSessionId);
      inFlightIdRef.current = null;
      lastSnapshotRef.current = { uploaded: 0, analyzed: 0, total: 0 };
    }
    resetV2();
    // `resetV2` remet `v2Step` à 'intro' ; `startV2()` rebascule sur 'ingesting'
    // avec un état neuf. On laisse le useEffect existant faire le relais —
    // mais on l'appelle aussi explicitement pour éviter un render vide.
    startV2();
  };

  // Reset déclenché depuis l'extérieur (menu Header → "Demande de CG").
  // Header dépose `form_cgia_force_fresh=1` dans sessionStorage avant la
  // navigation. On consomme la clé à chaque arrivée (location.key change
  // même quand on re-navigue vers la même route) et on délègue à
  // handleResetForm pour conserver le confirm + cancel BG upload.
  useEffect(() => {
    if (sessionStorage.getItem('form_cgia_force_fresh') !== '1') return;
    if (isEditMode) {
      sessionStorage.removeItem('form_cgia_force_fresh');
      return;
    }
    sessionStorage.removeItem('form_cgia_force_fresh');
    handleResetForm();
  }, [location.key, isEditMode]);

  // "Nouvelle demande de CG" qui PRÉSERVE l'analyse en cours en arrière-plan.
  // Contrairement à handleResetForm, on n'ANNULE PAS l'in-flight courant : on
  // fige son snapshot (pour qu'un "Voir le dossier" ultérieur depuis le tracker
  // recharge un état complet), puis on démarre une session vierge. Le dossier
  // en cours continue de tourner et reste visible dans le BackgroundUploadsTracker.
  // Miroir exact de BackgroundUploadsTracker.handleNewCg.
  const handleNewSession = () => {
    if (storeActiveInFlightId) {
      const snapshot = serializeV2Snapshot();
      void patchUpload(storeActiveInFlightId, { payload: snapshot } as any);
    }
    resetV2();
    startV2();
  };

  // Les points d'entrée "Demande de CG" (menu Header, accueil agence, listes)
  // déposent `form_cgia_new_session=1` dans sessionStorage avant de naviguer
  // vers /form-cg (→ /form-cgia). Sans ce relais, FormCGIAv2 relisait la session
  // persistée (sessionStorage) et réaffichait l'analyse EN COURS au lieu d'un
  // formulaire vierge (bug "analyse simultanée"). On consomme la clé à chaque
  // arrivée (location.key) et on démarre une session neuve sans toucher au
  // dossier en cours, qui continue en arrière-plan.
  useEffect(() => {
    if (sessionStorage.getItem('form_cgia_new_session') !== '1') return;
    sessionStorage.removeItem('form_cgia_new_session');
    if (isEditMode) return;
    handleNewSession();
  }, [location.key, isEditMode]);

  // Certification (popup) + notification de recalcul tarifaire. La modale est
  // le dernier verrou avant la génération de facture (étape recap), déclenchée
  // soit depuis l'étape de vérification (plaque/dept inchangés), soit après
  // re-validation du tarif recalculé.
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [recalcNotice, setRecalcNotice] = useState<string | null>(null);
  const requestFinalSubmission = () => setCertifyOpen(true);

  return (
    <Box sx={{ backgroundColor: tokens.color.bg, minHeight: '100vh', padding: '28px 36px' }}>
      <Box sx={{ maxWidth: 1240, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Toolbar en-tête : bouton "Recommencer" tout en haut à droite.
            Permet à l'agence de reprendre une soumission à zéro quand elle
            revient sur la page avec une session en cours (sessionStorage). */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
          <ThemeProvider theme={legacyTheme}>
            <Button
              className="secondaryDealActionButton"
              size="small"
              onClick={handleResetForm}
              startIcon={<RestartAltIcon />}
            >
              Recommencer à zéro
            </Button>
          </ThemeProvider>
        </Box>
        {/* Blocs du haut masqués sur l'étape de vérification : elle reprend le
            cœur de l'écran /edit/ SANS bandeau récap ni score de confiance. */}
        {v2Step !== 'verify' && (
          <>
            <HeroCard
              regPlate={regPlate}
              vehicleSummary={vehicleSummary}
              docsReceived={completedCount}
              docsExpected={totalCount}
              alertsCount={alertsCount}
              aiConfidence={specV1FlagOn ? null : aiConfidence}
              reviewStatus="none"
              editingRegistrationId={editingRegistrationId}
              requestedCount={openRequestedDocTypeCount}
            />

            <ParcoursBanner
              parcours={parcours}
              details={detectedParcoursSource ? PARCOURS_SOURCE_DETAILS[detectedParcoursSource] : null}
              isAnalyzing={activeJobsCount > 0}
              onCorrectDeclaration={detectedParcoursSource === 'agence' ? handleCorrectDeclaration : undefined}
            />
          </>
        )}

        {v2Step === 'plate_and_dept' ? (
          <PlateAndDepartmentScreen />
        ) : v2Step === 'tax_confirm' ? (
          <TaxConfirmationScreen prestationHT={prestationHT} onAcceptFinal={requestFinalSubmission} />
        ) : v2Step === 'verify' ? (
          <VerifyInfoScreen
            onRequestFinalSubmission={requestFinalSubmission}
            onNotify={(message) => setRecalcNotice(message)}
          />
        ) : v2Step === 'recap' ? (
          <RecapAgencyScreen />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: '20px',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <MultiDropzone onFilesAdded={handleFilesAdded} />
                {activeJobsCount > 0 && (
                  <Box
                    className="sk-fade-in-up"
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '14px 16px',
                      backgroundColor: tokens.color.blueLight,
                      border: `0.5px solid ${tokens.color.blueBorder}`,
                      borderRadius: `${tokens.radius.lg}px`,
                    }}
                  >
                    <Box
                      className="sk-spin"
                      sx={{
                        flexShrink: 0,
                        marginTop: '2px',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: `2px solid ${tokens.color.blueBorder}`,
                        borderTopColor: tokens.color.blue,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: tokens.color.blueDark, fontWeight: 700, lineHeight: 1.3 }}
                      >
                        {activeJobsCount} document{activeJobsCount > 1 ? 's' : ''} en cours d'analyse…
                      </Typography>
                      <Typography
                        key={loadingMessage}
                        className="sk-fade-in-up"
                        variant="caption"
                        sx={{
                          color: tokens.color.blueDark,
                          opacity: 0.85,
                          display: 'block',
                          marginTop: '4px',
                          fontStyle: 'italic',
                        }}
                      >
                        {loadingMessage}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: tokens.color.textMuted,
                          display: 'block',
                          marginTop: '6px',
                        }}
                      >
                        L'analyse IA peut prendre jusqu'à ~30 s par document (lecture approfondie + validation croisée).
                        Vous pouvez déjà en déposer d'autres.
                      </Typography>
                    </Box>
                  </Box>
                )}
                <DocumentChecklistColumn
                  rows={docRows}
                  completedCount={completedCount}
                  totalCount={totalCount}
                  requestedCount={openRequestedDocTypeCount}
                  receivedUploads={receivedUploads}
                  cotitulaireAcheteurActif={cotitulaireFlags.acheteurActif}
                  onToggleCotitulaireAcheteur={toggleCotitulaireAcheteur}
                  cotitulaireVendeurActif={cotitulaireFlags.vendeurActif}
                  cotitulaireVendeurNom={cotitulaireFlags.vendeurNom}
                  onToggleCotitulaireVendeur={toggleCotitulaireVendeur}
                  cotitulaireCniFileName={cotitulaireFileName}
                  coBuyerFiles={coBuyerFiles}
                  onCoBuyerFilesChange={handleCoBuyerFilesChange}
                  onReassignIdentityPerson={reassignIdentityPerson}
                />
              </Box>
              <AlertsColumn
                findings={effectiveFindings}
                // Tant qu'au moins une analyse OCR tourne OU que le moteur
                // v1 attend sa réponse Claude, on masque les findings
                // intermédiaires (qui s'affineraient en cours de route et
                // donneraient un effet "flou" inquiétant) et on montre un
                // loader sobre. Verdict final = analyses OCR finies ET v1
                // arrivé (success/echec) — auquel cas la cascade aval
                // (v1 → Opus → regex) rend le verdict définitif.
                // Loader uniquement pendant l'analyse OCR des pièces en cours
                // d'upload. v1 LLM peut tarder 20-60s ; en mode édition (pieces
                // déjà persistées), attendre v1 bloque le verdict regex qui est
                // déjà calculé. Les findings v1 se mélangent dans l'union
                // dédupée quand v1 résout, sans masquer le verdict initial.
                isAnalyzing={activeJobsCount > 0}
                // Vue agence (isGod=false) : les mineures sont affichées sous le
                // label « info ». Un god testant un dossier voit les vrais labels.
                isGod={isGod}
              />
            </Box>

            {/* ── Bannière rassurante "Skipcar s'en occupe" ─────────
                Visible dès que des alertes (errors OU warnings) ont été
                détectées par les cross-checks ET que l'agence a atteint
                le quota minimum de pièces. Objectif UX : déstresser
                l'agence — l'IA a flaggé mais ce n'est PAS bloquant, le
                pôle CG humain vérifie tout avant ANTS.
                IMPORTANT : masquée tant que l'analyse tourne (même condition
                que le "Analyse en cours" d'AlertsColumn, isAnalyzing =
                activeJobsCount > 0). Sinon on annonce "1 point à vérifier —
                continuez sereinement" sur un compte d'alertes non définitif
                qui peut encore changer quand v1/Opus résolvent. */}
            {alertsCount > 0 && completedCount >= MIN_DOCS_TO_CONTINUE && activeJobsCount === 0 && (
              <Box
                className="sk-fade-in-up"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '18px',
                  padding: '20px 22px',
                  borderRadius: `${tokens.radius['2xl']}px`,
                  background: hasMajorAlerts
                    ? `linear-gradient(135deg, ${tokens.color.redLight} 0%, #FFFFFF 80%)`
                    : `linear-gradient(135deg, ${tokens.color.orangeLight} 0%, #FFFFFF 80%)`,
                  border: `1px solid ${hasMajorAlerts ? tokens.color.redBorder : tokens.color.orangeBorder}`,
                  boxShadow: hasMajorAlerts ? '0 6px 20px rgba(239,68,68,0.10)' : '0 6px 20px rgba(249,115,22,0.08)',
                  marginTop: '4px',
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: hasMajorAlerts
                      ? `linear-gradient(135deg, ${tokens.color.red} 0%, ${tokens.color.redDark} 100%)`
                      : `linear-gradient(135deg, ${tokens.color.orange} 0%, ${tokens.color.orangeDark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: hasMajorAlerts ? '0 4px 12px rgba(220,38,38,0.35)' : '0 4px 12px rgba(234,88,12,0.35)',
                  }}
                >
                  <VerifiedUserRoundedIcon sx={{ fontSize: 26, color: '#FFFFFF' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '10.5px',
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: hasMajorAlerts ? tokens.color.redDark : tokens.color.orangeDark,
                    }}
                  >
                    {hasMajorAlerts ? "À corriger avant l'envoi" : "Skipcar s'en occupe"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '17px',
                      fontWeight: 800,
                      color: tokens.color.dark,
                      letterSpacing: '-0.01em',
                      marginTop: '2px',
                      lineHeight: 1.25,
                    }}
                  >
                    {hasMajorAlerts
                      ? `${majorAlertsCount} point${majorAlertsCount > 1 ? 's' : ''} important${
                          majorAlertsCount > 1 ? 's' : ''
                        } à corriger${
                          minorAlertsCount > 0
                            ? ` · ${minorAlertsCount} point${minorAlertsCount > 1 ? 's' : ''} à vérifier`
                            : ''
                        }`
                      : `${minorAlertsCount} point${
                          minorAlertsCount > 1 ? 's' : ''
                        } à vérifier — vous pouvez continuer sereinement`}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13.5px',
                      fontWeight: 500,
                      color: tokens.color.textSecondary,
                      marginTop: '6px',
                      lineHeight: 1.5,
                    }}
                  >
                    {hasMajorAlerts
                      ? "Vous pouvez quand même soumettre, mais ces points seront très probablement renvoyés par l'ANTS s'ils ne sont pas corrigés. Notre pôle expert vérifie chaque dossier et vous sollicitera si besoin."
                      : "Notre pôle expert relit chaque dossier avant l'envoi à l'ANTS. Aucune alerte ne bloque votre soumission — nous corrigerons ou vous solliciterons si besoin."}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ── Sticky CTA footer ──────────────────────────────
                Comportement contextuel :
                  - Mode édition : "Renvoyer les N pièces →" + compteur X/N
                    pièces redéposées + statut cible (paiement vs review).
                  - Mode normal, à 3 états (submitFooterState) :
                      collecting  → discret (quota non atteint / analyses en cours)
                      submittable → barre ambre "vous pouvez soumettre" + gros CTA
                      complete    → barre verte "dossier complet" + gros CTA
                    La soumission sur dossier incomplet étant permise, le footer
                    devient le guide visuel dès que le quota est atteint. */}
            <Box
              key={isEditMode ? 'edit' : submitFooterState}
              className={!isEditMode && submitFooterState !== 'collecting' ? 'sk-fade-in-up' : undefined}
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                marginTop: '8px',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: '10px',
                ...(isEditMode
                  ? {
                      backgroundColor: tokens.color.bg,
                      padding: '12px 0',
                      borderTop: `2px solid ${tokens.color.orange}`,
                    }
                  : submitFooterState === 'collecting'
                    ? {
                        backgroundColor: tokens.color.bg,
                        padding: '12px 0',
                        borderTop: `1px solid ${tokens.color.border}`,
                      }
                    : submitFooterState === 'submittable'
                      ? {
                          backgroundColor: tokens.color.amberLight,
                          padding: '16px 20px',
                          border: `1px solid ${tokens.color.orangeBorder}`,
                          borderRadius: `${tokens.radius.xl}px ${tokens.radius.xl}px 0 0`,
                          borderBottom: 'none',
                          boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.10)',
                        }
                      : {
                          backgroundColor: tokens.color.greenLight,
                          padding: '16px 20px',
                          border: `1px solid ${tokens.color.greenBorder}`,
                          borderRadius: `${tokens.radius.xl}px ${tokens.radius.xl}px 0 0`,
                          borderBottom: 'none',
                          boxShadow: '0 -10px 30px rgba(34, 197, 94, 0.16)',
                        }),
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isEditMode ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: tokens.color.orangeDark,
                      }}
                    >
                      {readyToFulfillCount} / {openRequestedDocTypeCount} pièce
                      {openRequestedDocTypeCount > 1 ? 's' : ''} redéposée
                      {readyToFulfillCount > 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
                      {allRequestsReady
                        ? nextStatusOnFulfill === 'awaiting_payment'
                          ? 'Après envoi, votre dossier passera en attente de paiement.'
                          : 'Après envoi, Skipcar va relire votre dossier.'
                        : someRequestsReady
                          ? `Vous pouvez envoyer dès maintenant ce que vous avez : le dossier restera en attente ${
                              openRequestedDocTypeCount - readyToFulfillCount === 1
                                ? 'de la pièce manquante'
                                : `des ${openRequestedDocTypeCount - readyToFulfillCount} pièces manquantes`
                            }.`
                          : 'Déposez les pièces signalées « À redéposer » dans la liste ci-dessus.'}
                    </Typography>
                    {fulfillError && (
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.color.red, display: 'block', marginTop: '4px' }}
                      >
                        {fulfillError}
                      </Typography>
                    )}
                  </Box>
                ) : submitFooterState === 'collecting' ? (
                  <>
                    {completedCount < MIN_DOCS_TO_CONTINUE && (
                      <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
                        Encore {MIN_DOCS_TO_CONTINUE - completedCount} pièce
                        {MIN_DOCS_TO_CONTINUE - completedCount > 1 ? 's' : ''} à déposer pour pouvoir soumettre (
                        {MIN_DOCS_TO_CONTINUE} / {totalCount} minimum).
                      </Typography>
                    )}
                    {completedCount >= MIN_DOCS_TO_CONTINUE && activeJobsCount > 0 && (
                      <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
                        Analyse IA en cours sur {activeJobsCount} pièce
                        {activeJobsCount > 1 ? 's' : ''} — encore un instant…
                      </Typography>
                    )}
                  </>
                ) : submitFooterState === 'submittable' ? (
                  <Box>
                    <Typography
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: 15,
                        fontWeight: 800,
                        color: tokens.color.amberDeep,
                        lineHeight: 1.3,
                      }}
                    >
                      <BoltRoundedIcon sx={{ fontSize: 18 }} />
                      Vous pouvez soumettre dès maintenant
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: tokens.color.textSecondary, display: 'block', marginTop: '2px' }}
                    >
                      {missingDocTitles.length > 0
                        ? `Il manque : ${missingDocTitles.join(', ')} — Skipcar vous ${
                            missingDocTitles.length > 1 ? 'les' : 'le'
                          } redemandera si nécessaire.`
                        : 'Les pièces signalées « à vérifier » seront relues par notre pôle expert.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: 15,
                        fontWeight: 800,
                        color: tokens.color.greenDeep,
                        lineHeight: 1.3,
                      }}
                    >
                      <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
                      Toutes les pièces sont là
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: tokens.color.textSecondary, display: 'block', marginTop: '2px' }}
                    >
                      Vérifiez les informations extraites avant l'envoi.
                    </Typography>
                  </Box>
                )}
              </Box>
              <ThemeProvider theme={legacyTheme}>
                {isEditMode ? (
                  <Button
                    className="dealActionButton"
                    onClick={() => void fulfillOpenRequests()}
                    disabled={!someRequestsReady || fulfillSubmitting}
                    color="primary"
                    variant="contained"
                    startIcon={
                      fulfillSubmitting ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined
                    }
                    sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                  >
                    {fulfillSubmitting
                      ? 'Envoi en cours…'
                      : allRequestsReady
                        ? `Renvoyer ${
                            openRequestedDocTypeCount === 1 ? 'la pièce' : `les ${openRequestedDocTypeCount} pièces`
                          } →`
                        : `Renvoyer ${readyToFulfillCount === 1 ? 'la pièce déposée' : `les ${readyToFulfillCount} pièces déposées`} →`}
                  </Button>
                ) : (
                  <Button
                    className="dealActionButton"
                    onClick={() => setV2Step('verify')}
                    disabled={!canContinue}
                    color="primary"
                    variant="contained"
                    sx={{
                      alignSelf: { xs: 'stretch', sm: 'auto' },
                      // États proéminents : le CTA primaire doit dominer la
                      // page (et notamment battre le bouton orange du tracker).
                      ...(submitFooterState !== 'collecting' && {
                        fontSize: '16px',
                        padding: '12px 28px',
                        flexShrink: 0,
                      }),
                    }}
                  >
                    Vérifier les informations →
                  </Button>
                )}
              </ThemeProvider>
            </Box>
          </>
        )}
      </Box>

      {/* Toast pédagogique T+5s — "l'analyse continue, vous pouvez naviguer". */}
      <Snackbar
        open={bgHintOpen}
        autoHideDuration={9000}
        onClose={() => setBgHintOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setBgHintOpen(false)}
          sx={{
            fontSize: '14px',
            backgroundColor: tokens.color.surface,
            border: `1px solid ${tokens.color.border}`,
            color: tokens.color.dark,
          }}
        >
          L'analyse continue en arrière-plan — vous pouvez lancer une autre demande ou naviguer ailleurs, le tracker en
          bas-gauche garde le fil.
        </Alert>
      </Snackbar>

      {/* Notification de recalcul tarifaire (plaque/département modifiés à
          l'étape de vérification). Persiste au changement d'étape vers
          tax_confirm. */}
      <Snackbar
        open={recalcNotice !== null}
        autoHideDuration={8000}
        onClose={() => setRecalcNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setRecalcNotice(null)}
          sx={{
            fontSize: '14px',
            backgroundColor: tokens.color.surface,
            border: `1px solid ${tokens.color.border}`,
            color: tokens.color.dark,
          }}
        >
          {recalcNotice}
        </Alert>
      </Snackbar>

      {/* Popup de certification — dernier verrou avant la génération de facture. */}
      <CertifySubmissionModal
        open={certifyOpen}
        onClose={() => setCertifyOpen(false)}
        onConfirm={() => {
          setCertifyOpen(false);
          setV2Step('recap');
        }}
      />
    </Box>
  );
};

const FormCGIAv2 = () => (
  <NextThemeProvider>
    <FormCGIAv2Inner />
  </NextThemeProvider>
);

export default FormCGIAv2;
